/**
 * Utilities for automating radio button settings on Instagram.
 * Includes generating stage definitions.
 *
 * Instagram implements radio buttons in two different ways:
 * - as <div> elements with role="button"
 * - as <input> elements
 */

import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import { InstagramRecommendationKey, StageType } from '../../utils/enums'
import { _debug } from '../../utils/logging'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import {
  getAttrPromise,
  getPeriodicCheck,
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import {
  evaluateXpath,
  getCommonAncestor,
  getNearestElementMatch,
} from '../interruptibleXpath'

export enum InstagramRadioButtonType {
  ButtonDiv,
  Input,
}

function getRadioButtonRule(radioButtonType: InstagramRadioButtonType) {
  switch (radioButtonType) {
    case InstagramRadioButtonType.ButtonDiv:
      return '*[@role = "button"]'
    case InstagramRadioButtonType.Input:
      return 'input[@type != "checkbox"]'
    default:
      throw new Error(`Do not recognize type ${radioButtonType}`)
  }
}

export async function waitForSectionElement(
  label: string,
  radioButtonType: InstagramRadioButtonType,
): Promise<HTMLElement> {
  // NOTE: Add timeout?
  const sectionLabelElement =
    await getWaitForElementToAppearPromise<HTMLElement>(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `descendant::*[text()="${label}" and 
          (ancestor::*[@role="heading"] or self::h1 or self::h2 or self::h3 or self::span) and
          not(ancestor::a)]`,
        )[0],
      5000,
    )

  if (!sectionLabelElement) {
    throw new SectionNotFoundException(
      `Could not find section with label ${label}`,
    )
  }

  const radioButtonRule = getRadioButtonRule(radioButtonType)
  const nearestRadioButton =
    await getWaitForElementToAppearPromise<HTMLElement>(
      sectionLabelElement,
      (node) =>
        getNearestElementMatch<HTMLElement>(
          node as HTMLElement,
          radioButtonRule,
          'preceding',
        ),
    )

  if (!nearestRadioButton) {
    throw new Error(
      `Could not find radio button for section with label ${label}`,
    )
  }

  const sectionElement = getCommonAncestor<HTMLElement>(
    sectionLabelElement,
    nearestRadioButton,
  )

  return sectionElement
}

function getAllRadioButtons(
  root: Node,
  radioButtonType: InstagramRadioButtonType,
): HTMLElement[] {
  const radioButtonRule = getRadioButtonRule(radioButtonType)
  return evaluateXpath<HTMLElement>(root, `descendant::${radioButtonRule}`)
}

function getLeafElements(rootElement: HTMLElement): HTMLElement[] {
  // TODO: this should have a test
  if (!rootElement.children || rootElement.children.length === 0) {
    return [rootElement]
  }

  return Array.from(rootElement.children).flatMap(getLeafElements)
}

function getLabelForRadioButtonButtonDiv(radioButton: HTMLElement): string {
  const leafElements = getLeafElements(radioButton)
  const labelCandidates = leafElements.map((x) => x.textContent).filter(Boolean)

  return labelCandidates[0]
}

function getLabelForRadioButtonInput(radioButton: HTMLElement): string {
  const firstNodeWithText = getNearestElementMatch(
    radioButton,
    '*[text()]',
    'preceding',
  )

  if (!firstNodeWithText) {
    throw new Error('Could not find any text nodes')
  }

  return firstNodeWithText.textContent
}

function getLabelForRadioButton(
  radioButton: HTMLElement,
  radioButtonType: InstagramRadioButtonType,
): string {
  switch (radioButtonType) {
    case InstagramRadioButtonType.ButtonDiv:
      return getLabelForRadioButtonButtonDiv(radioButton)
    case InstagramRadioButtonType.Input:
      return getLabelForRadioButtonInput(radioButton)
    default:
      throw new Error(`Do not recognize type ${radioButtonType}`)
  }
}

function isGrayscale(rgb: string): boolean {
  // Tests that
  // - string is of form `rgb(<number>, <number>, <number>)`
  // - all three numbers are equal
  const rgbPat = /^rgb\((\d+),\s*\1,\s*\1\)$/
  const rgbaPat = /^rgba\((\d+),\s*\1,\s*\1,\s*\d+\)$/
  return rgbPat.test(rgb) || rgbaPat.test(rgb)
}

/**
 * Currently, IG's radio buttons are divs with inline styles declaring, in
 * particular, the background color.
 *
 * When the buttons are unselected, the leaf nodes are all shades of gray (i.e.,
 * all three RGB values are equal); they are different when selected.
 */
function getIsSelectedButtonDiv(radioButton: HTMLElement): boolean {
  const leafElements: HTMLElement[] = getLeafElements(radioButton)

  const backgroundColors: string[] = leafElements
    .map((e) => e.computedStyleMap().get('background-color').toString())
    .filter((x) => Boolean(x))

  if (backgroundColors.length === 0) {
    throw new Error(
      `Did not find expected elements (no elements with background-color style attr)`,
    )
  }

  const isAllRgb = backgroundColors.every(
    (x) => x.startsWith('rgb(') || x.startsWith('rgba('),
  )
  if (!isAllRgb) {
    throw new Error(`Did not find rgb background-colors as expected`)
  }

  const isAllGrayscale = backgroundColors.every(isGrayscale)

  return !isAllGrayscale
}

function getIsSelectedInput(radioButton: HTMLElement): boolean {
  const ariaCheckedValue = (radioButton as HTMLInputElement).ariaChecked
  if (ariaCheckedValue) {
    return ariaCheckedValue === 'true'
  }
  return (radioButton as HTMLInputElement).checked
}

function getIsSelected(
  radioButton: HTMLElement,
  radioButtonType: InstagramRadioButtonType,
): boolean {
  switch (radioButtonType) {
    case InstagramRadioButtonType.ButtonDiv:
      return getIsSelectedButtonDiv(radioButton)
    case InstagramRadioButtonType.Input:
      return getIsSelectedInput(radioButton)
    default:
      throw new Error(`Do not recognize type ${radioButtonType}`)
  }
}

async function getCurrentValueForSection(
  label: string,
  radioButtonType: InstagramRadioButtonType,
): Promise<string> {
  _debug(
    `Looking for value for section ${label}`,
    'automation stage machine',
    'd',
  )

  const sectionElement = await waitForSectionElement(label, radioButtonType)
  const radioButtonElements = getAllRadioButtons(
    sectionElement,
    radioButtonType,
  )

  const selectedElementCandidates = radioButtonElements.filter((x) =>
    getIsSelected(x, radioButtonType),
  )

  if (selectedElementCandidates.length === 0) {
    throw new Error(
      `Could not find selected radio button for section with label ${label}`,
    )
  }

  if (selectedElementCandidates.length > 1) {
    throw new Error(
      `Found multiple selected radio buttons for section with label ${label}`,
    )
  }

  const selectedElement = selectedElementCandidates[0]

  return getLabelForRadioButton(selectedElement, radioButtonType)
}

function waitForRadioButtonToUpdateAfterClickButtonDiv(
  radioButton: HTMLElement,
): Promise<void> {
  const leafElements = getLeafElements(radioButton)

  if (leafElements.length === 0) {
    throw new Error(`Did not find any leaf elements`)
  }

  const attributePromises = leafElements.map((e) => getAttrPromise(e, 'style'))

  return Promise.any(attributePromises).then(() => {}) // void the return value
}

function waitForRadioButtonToUpdateAfterClickInput(
  input: HTMLElement,
): Promise<void> {
  // Wait for input element to become checked/unchecked
  return Promise.race([
    getAttrPromise(input, 'aria-checked'), // void the return value
    wait(500),
  ]).then(() => {})
}

function waitForRadioButtonToUpdateAfterClick(
  radioButton: HTMLElement,
  radioButtonType: InstagramRadioButtonType,
): Promise<void> {
  switch (radioButtonType) {
    case InstagramRadioButtonType.ButtonDiv:
      return waitForRadioButtonToUpdateAfterClickButtonDiv(radioButton)
    case InstagramRadioButtonType.Input:
      return waitForRadioButtonToUpdateAfterClickInput(radioButton)
    default:
      throw new Error(`Do not recognize type ${radioButtonType}`)
  }
}

function setGatherAutomationError(
  updateStoredAutomationErrorsFn: (
    value: { [key: string]: any },
    key: string,
  ) => Promise<void>,
  settingKey: string,
  error: Error,
  recommendationKey: InstagramRecommendationKey,
) {
  return updateStoredAutomationErrorsFn(
    {
      [settingKey]: {
        gatherError: { message: error.message, name: error.name },
      },
    },
    recommendationKey,
  )
}

/**
 * Remove non-alphanumeric characters to make comparisons easier (e.g.,
 * different types of quotation characters can make it hard).
 */
export function sanitizeLabel(label: string): string {
  const pat = /[^0-9a-z]+/gi // remove all non alphanumeric characters
  return label.replace(pat, '')
}

async function updateValueForSection(
  label: string,
  value: string,
  isDry: boolean,
  radioButtonType: InstagramRadioButtonType,
): Promise<void> {
  // Do nothing if already set
  const currentValue = await getCurrentValueForSection(label, radioButtonType)
  if (currentValue === value) {
    return
  }

  const sectionElement = await waitForSectionElement(label, radioButtonType)
  const radioButtonElements = getAllRadioButtons(
    sectionElement,
    radioButtonType,
  )

  const matchingElementCandidates = radioButtonElements.filter(
    (e) =>
      sanitizeLabel(
        getLabelForRadioButton(e, radioButtonType),
      ).toLowerCase() === sanitizeLabel(value).toLowerCase(),
  )

  if (matchingElementCandidates.length === 0) {
    throw new Error(`Could not find radio button with label ${value}`)
  }

  if (matchingElementCandidates.length > 1) {
    throw new Error(`Found multiple radio buttons with label ${value}`)
  }

  const matchingElement = matchingElementCandidates[0]
  if (!matchingElement) {
    throw new Error(
      `Matching radio button is null or undefined on Instagram for label ${label}`,
    )
  }

  if (isDry) {
    return
  }

  const updatePromise = waitForRadioButtonToUpdateAfterClick(
    matchingElement,
    radioButtonType,
  )
  switch (radioButtonType) {
    case InstagramRadioButtonType.ButtonDiv:
      matchingElement.click()
      break
    case InstagramRadioButtonType.Input: {
      // Click the label not the button because Instagram is quirky
      const element = getNearestElementMatch(
        matchingElement,
        '*[text()]',
        'preceding',
      )
      if (!element) {
        throw new Error(
          `Could not find matching radio button label on Instagram for label ${label}`,
        )
      }
      element.click()
      break
    }
    default:
      throw new Error(`Do not recognize type ${radioButtonType}`)
  }

  await updatePromise

  // Some settings have a modal asking users if they really want to do
  // something
  const modalEl = evaluateXpath<HTMLDivElement>(
    document,
    '//div[@role="dialog"]//div[@role="dialog"]',
  )[0]
  if (modalEl) {
    // If one pops up, we want to click the top option
    const buttonEl = evaluateXpath<HTMLButtonElement>(
      document,
      '//div[@role="dialog"]//div[@role="dialog"]//button',
    )[0]
    if (buttonEl) {
      buttonEl.click()
      await getPeriodicCheck(1000, () => {
        const disappearingModalEl = evaluateXpath<HTMLDivElement>(
          document,
          '//div[@role="dialog"]//div[@role="dialog"]',
        )[0]
        if (!disappearingModalEl) {
          return true
        }
        return false
      })
    }
  }
}

// Wrappers

async function getAndSaveRadioButtonOptionValuesWithKeys(
  settingsKeys: string[],
  settingsKeysToLabelsFn: (key: string) => string,
  saveErrorFn: (key: any, error: Error) => Promise<void>,
  saveFn: (value: any) => Promise<void>,
  radioButtonType: InstagramRadioButtonType,
): Promise<void> {
  const currentValuePromises = settingsKeys.map((x) =>
    getCurrentValueForSection(settingsKeysToLabelsFn(x), radioButtonType),
  )

  const saveObj = {}
  for (let i = 0; i < currentValuePromises.length; i += 1) {
    const key = settingsKeys[i]
    const valuePromise = currentValuePromises[i]

    try {
      saveObj[key] = await valuePromise
    } catch (error) {
      await saveErrorFn(key, error)
    }
  }

  await saveFn(saveObj)
}

async function updateRadioButtonOptionsWithKeys(
  settingsKeys: string[],
  saveErrorFn: (key: any, error: Error) => Promise<void>,
  settingsKeyToLabelFn: (key: string) => string,
  settingsKeyToTargetValueFn: (key: string) => string,
  settingsKeyToIsDisabledFn: (key: string) => boolean,
  settingsKeyToIsSelectedFn: (key: string) => boolean,
  shouldUpdateFn: (currentValue: any, targetValue: any, key: string) => boolean,
  isDry: boolean,
  radioButtonType: InstagramRadioButtonType,
) {
  // We deliberately do updates one section at a time
  for (const setting of settingsKeys) {
    try {
      const isSelected = settingsKeyToIsSelectedFn(setting)
      const isDisabled = settingsKeyToIsDisabledFn(setting)
      const label = settingsKeyToLabelFn(setting)

      if (isSelected && !isDisabled) {
        const currentValue = await getCurrentValueForSection(
          label,
          radioButtonType,
        )
        const targetValue = settingsKeyToTargetValueFn(setting)

        if (shouldUpdateFn(currentValue, targetValue, setting)) {
          await updateValueForSection(
            label,
            targetValue,
            isDry,
            radioButtonType,
          )
        }
      }
    } catch (error) {
      await saveErrorFn(setting, error)
    }
  }
}

async function undoRadioButtonOptionsWithKeys(
  settingsKeys: string[],
  saveErrorFn: (key: any, error: Error) => Promise<void>,
  settingsKeyToLabelsFn: (key: string) => string,
  initialValues: { [key: string]: string },
  settingsKeyToIsDisabledFn: (key: string) => boolean,
  settingsKeyToIsSelectedFn: (key: string) => boolean,
  isDry: boolean,
  radioButtonType: InstagramRadioButtonType,
) {
  const mapSectionLabelToTargetValue = {}
  for (const setting of settingsKeys) {
    mapSectionLabelToTargetValue[setting] = initialValues[setting]
  }

  return updateRadioButtonOptionsWithKeys(
    settingsKeys,
    saveErrorFn,
    settingsKeyToLabelsFn,
    (x) => mapSectionLabelToTargetValue[x],
    settingsKeyToIsDisabledFn,
    settingsKeyToIsSelectedFn,
    () => true,
    isDry,
    radioButtonType,
  )
}

export function generateRadioButtonStageDefs(
  recommendationKey: InstagramRecommendationKey,
  settingsKeys: string[],
  settingsKeyToLabelFn: (key: string) => string,
  settingsKeyToTargetValueFn: (key: string) => string,
  shouldUpdateFn: (currentValue: any, targetValue: any, key: string) => boolean,
  gatherStageKey: string,
  updateStageKey: string,
  undoStageKey: string,
  url: string,
  radioButtonType: InstagramRadioButtonType,
) {
  const getSettingIsSelectedFn =
    (settingsSelected: { [key: string]: boolean }) => (x) =>
      settingsSelected[x]

  const getSaveErrorFn =
    (updateStoredAutomationErrorFn) => (settingKey, error) =>
      setGatherAutomationError(
        updateStoredAutomationErrorFn,
        settingKey,
        error,
        recommendationKey,
      )

  const gatherStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: gatherStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const { updateStoredAutomationErrors, updateStoredInitialValues } =
          userContext
        await getAndSaveRadioButtonOptionValuesWithKeys(
          settingsKeys,
          settingsKeyToLabelFn,
          getSaveErrorFn(updateStoredAutomationErrors),
          (x) => updateStoredInitialValues(x, recommendationKey),
          radioButtonType,
        )
      },
    },
  ]

  const updateStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: updateStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const {
          automationErrors,
          isDry,
          settingsSelected,
          updateStoredAutomationErrors,
        } = userContext

        const settingsKeyToIsDisabledFn = (settingKey) =>
          automationErrors && settingKey in automationErrors

        await updateRadioButtonOptionsWithKeys(
          settingsKeys,
          getSaveErrorFn(updateStoredAutomationErrors),
          settingsKeyToLabelFn,
          settingsKeyToTargetValueFn,
          settingsKeyToIsDisabledFn,
          getSettingIsSelectedFn(settingsSelected),
          shouldUpdateFn,
          isDry,
          radioButtonType,
        )
      },
    },
  ]

  const undoStageDefs: StageDef[] = [
    {
      type: StageType.ACTION,
      key: undoStageKey,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
      url,
      action: async (machineContext, userContext) => {
        const {
          isDry,
          automationErrors,
          initialValues,
          settingsSelected,
          updateStoredAutomationErrors,
        } = userContext

        const settingsKeyToIsDisabledFn = (settingKey) =>
          automationErrors && settingKey in automationErrors

        await undoRadioButtonOptionsWithKeys(
          settingsKeys,
          getSaveErrorFn(updateStoredAutomationErrors),
          settingsKeyToLabelFn,
          initialValues,
          settingsKeyToIsDisabledFn,
          getSettingIsSelectedFn(settingsSelected),
          isDry,
          radioButtonType,
        )
      },
    },
  ]

  return {
    gatherStageDefs,
    updateStageDefs,
    undoStageDefs,
  }
}
